// The Agents section, the Models Agents sub-tab (SCR-576, SCR-590): one card per agent (Claude Code,
// Codex, Web chat, Other tools) over the draft, and the section's own Save, which writes the
// agents' fields with the version read (If-Match). The global list is the Families sub-tab's;
// this section never writes it.
import type { ApiError } from "../../api/errors";
import type { Surface } from "../../api/org-context";
import { HelpButton, type HelpId } from "../../primitives/help";
import { ErrorBanner } from "../../primitives/states";
import type { Json } from "../../settings/inherit";
import type { Pins } from "../../settings/pins";
import { AgentCard } from "./agent-card";
import type { AgentCardModel, AgentErrors, NarrowChoices } from "./agents-model";
import type { Harness } from "./model";

const HELP: HelpId = "settings.models.agents";

export interface AgentsSectionProps {
  /** The form's accessible name ("Model settings", or "… for <installation name>"; never an id), which the shells' browser suites find it by. */
  label: string;
  cards: readonly AgentCardModel[];
  narrow: Readonly<Record<Harness["key"], NarrowChoices>>;
  effective: Json | undefined;
  inherited: (path: string) => boolean;
  pins: Pins;
  surface: Surface;
  /** The last save's model refusals, by agent. */
  errors: Readonly<Partial<Record<Harness["key"], AgentErrors>>>;
  fieldErrors: Readonly<Record<string, string>>;
  /** A failure the cards do not place (412, 503, a schema refusal): the section's banner. */
  failure: ApiError | null;
  saving: boolean;
  /** Save is offered: something changed, nothing is invalid, the page is writable. */
  canSave: boolean;
  /** The read-only proxy's reason, as the button's title. */
  readOnlyReason: string | null;
  onChange(path: string, value: Json | undefined): void;
  onSave(): void;
}

export function AgentsSection({ label, cards, narrow, effective, inherited, pins, surface, errors, fieldErrors, failure, saving, canSave, readOnlyReason, onChange, onSave }: AgentsSectionProps) {
  return (
    <form
      aria-label={label}
      onSubmit={(event) => {
        event.preventDefault();
        if (canSave) onSave();
      }}
    >
      <div class="card-title-row">
        <h2 class="section-title">Agents</h2>
        <HelpButton topic={HELP} label="Agents" />
      </div>
      <p class="card-note">Each agent is offered the models Families allows, and may narrow them further. Its default is always one of them.</p>
      <div class="harness-grid">
        {cards.map((card) => (
          <AgentCard
            key={card.harness.key}
            card={card}
            narrow={narrow[card.harness.key]}
            effective={effective}
            inherited={inherited}
            pins={pins}
            surface={surface}
            errors={errors[card.harness.key] ?? {}}
            fieldErrors={fieldErrors}
            onChange={onChange}
          />
        ))}
      </div>
      {failure && <ErrorBanner error={failure} details={true} />}
      <div class="row">
        <button type="submit" class="button button-primary" disabled={!canSave} title={readOnlyReason ?? undefined} aria-describedby={readOnlyReason ? "models-stale-note" : undefined}>
          {saving ? "Saving" : "Save"}
        </button>
      </div>
    </form>
  );
}
